import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Quote, TrendingUp, Timer } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { site } from "@/data/site";

function CountUp({ to, suffix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} className={className}>
      {value}
      {suffix}
    </span>
  );
}

function StatsStrip() {
  return (
    <div
      className="mt-16 grid gap-px border-2 border-white/12 bg-white/12 lg:mt-24 lg:grid-cols-2"
      data-testid="results-stats-strip"
    >
      <Reveal className="bg-[#0D0714]">
        <div className="relative flex h-full flex-col justify-between overflow-hidden p-8 lg:p-12">
          <div className="halftone-purple pointer-events-none absolute inset-0" />
          <div className="relative flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-purple">
            <TrendingUp className="h-4 w-4" />
            Heart Performance
          </div>
          <p className="relative mt-6 font-display text-7xl font-black tracking-tighter sm:text-8xl lg:text-9xl">
            <CountUp
              to={50}
              className="text-purple drop-shadow-[0_0_30px_rgba(168,50,255,0.6)]"
            />
          </p>
          <p className="relative mt-4 font-display text-lg font-black uppercase tracking-tight text-white sm:text-xl">
            VO2 Max and climbing
          </p>
          <p className="relative mt-3 max-w-sm text-sm font-medium leading-relaxed text-white/60">
            A karting client's measured VO2 Max after training — proof the
            engine behind the wheel is getting stronger.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.12} className="bg-[#100D04]">
        <div className="relative flex h-full flex-col justify-between overflow-hidden p-8 lg:p-12">
          <div className="halftone-gold pointer-events-none absolute inset-0" />
          <div className="relative flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">
            <Timer className="h-4 w-4" />
            Stint Endurance
          </div>
          <p className="relative mt-6 font-display text-7xl font-black tracking-tighter sm:text-8xl lg:text-9xl">
            <CountUp
              to={25}
              className="text-gold drop-shadow-[0_0_30px_rgba(255,215,0,0.5)]"
            />
            <span className="ml-3 text-3xl font-black uppercase text-white sm:text-4xl lg:text-5xl">
              min
            </span>
          </p>
          <p className="relative mt-4 font-display text-lg font-black uppercase tracking-tight text-white sm:text-xl">
            Non-stop behind the wheel
          </p>
          <p className="relative mt-3 max-w-sm text-sm font-medium leading-relaxed text-white/60">
            Driving stints that were simply impossible before the program are
            now completed without lifting off.
          </p>
        </div>
      </Reveal>
    </div>
  );
}

const spanFor = (i) => (i < 3 ? "lg:col-span-2" : "lg:col-span-3");

export default function Testimonials() {
  return (
    <section id="results" className="relative py-28 lg:py-40" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal>
          <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold" />
            Results
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="max-w-3xl font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
            From the <span className="text-purple">driver's seat.</span>
          </h2>
        </Reveal>

        <StatsStrip />

        <div className="mt-16 grid gap-8 lg:mt-20 lg:grid-cols-6 lg:gap-6">
          {site.testimonials.map((t, i) => (
            <Reveal
              key={i}
              delay={i * 0.1}
              className={`${spanFor(i)} ${i === 1 ? "lg:translate-y-6" : ""} ${i === 4 ? "lg:-translate-y-4" : ""}`}
            >
              <figure
                className="relative flex h-full flex-col border-2 border-white/12 bg-surface p-8 lg:p-10"
                data-testid={`testimonial-card-${i}`}
              >
                <Quote
                  className={`h-10 w-10 ${i % 2 === 1 ? "text-gold" : "text-purple"}`}
                  fill="currentColor"
                  strokeWidth={0}
                />
                <blockquote className="mt-6 flex-1 text-lg font-semibold leading-relaxed text-white/85 lg:text-xl">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-8 border-t border-white/10 pt-5">
                  <p className="font-display text-sm font-black uppercase tracking-wide text-white" data-testid={`testimonial-name-${i}`}>
                    {t.name}
                  </p>
                  <p
                    className={`mt-1 text-[10px] font-black uppercase tracking-[0.25em] ${
                      i % 2 === 1 ? "text-gold" : "text-purple"
                    }`}
                    data-testid={`testimonial-role-${i}`}
                  >
                    {t.role}
                  </p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
